import { Injectable } from '@angular/core';

import { Address } from '@guardfinder/address';

import { map, Observable } from 'rxjs';

import { ApiClient } from '../../../api-client';
import { UnitResult } from '../../../error/Domain/unit-result';
import { IsGuardsAvailableService } from '../../Domain/Port/is-guards-available.service';

/**
 * Types for the backend API request and response.
 */
type BackendGuardsAvailableRequest = {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
};

type BackendGuardsAvailableResponse = {
    available: boolean;
};

/**
 * BackendGuardsAvailableService
 *
 * Concrete implementation of IsGuardsAvailableService that checks guard availability
 * via the backend API.
 *
 * Responsibilities:
 * - Transforms the Address object into the format expected by the backend API.
 * - Communicates with the '/verify-location' endpoint to check guard availability.
 * - Maps the API response to a UnitResult<boolean> for consistent error handling.
 *
 * Dependencies:
 * - ApiClient: Used to make HTTP requests to the backend.
 *
 * Usage:
 * - Injected into components like LeadFormComponent to check guard availability
 *   at a specific location before proceeding with lead or job creation.
 */
@Injectable({
    providedIn: 'root'
})
export class BackendGuardsAvailableService extends IsGuardsAvailableService {
    private readonly endpoint = '/verify-location';

    constructor(private readonly apiClient: ApiClient) {
        super();
    }

    /**
     * Checks if guards are available at the specified address by calling the backend API.
     *
     * @param clientLead The address to check for guard availability.
     * @returns An Observable of UnitResult<boolean> where:
     *          - true indicates guards are available at the location
     *          - false indicates no guards are available
     *          - UnitResult.failure indicates an error occurred during the check
     */
    execute(clientLead: Address): Observable<UnitResult<boolean>> {
        // Transform the Address object into the format expected by the backend
        const request: BackendGuardsAvailableRequest = {
            streetAddress: clientLead.street_number + ' ' + clientLead.street,
            city: clientLead.city,
            state: clientLead.state,
            zipCode: clientLead.zip
        };

        // Make the API request and transform the response
        return this.apiClient
            .post<BackendGuardsAvailableRequest, BackendGuardsAvailableResponse>(this.endpoint, request, false)
            .pipe(
                map(response =>
                    response.isSuccess()
                        ? UnitResult.success<boolean>(response.getResult().available)
                        : UnitResult.failure<boolean>(response.getErrors())
                )
            );
    }
}
